import { Citation } from "src/taskpane/helpers/Models";
import ReactDOM from "react-dom/client";

type RangeMap = {
  beforeRange: Word.Range;
  wordRange: Word.Range;
  afterRange: Word.Range;
  citation: Citation;
};

export const simpleRenderToString = async (element: React.ReactElement) => {
  const container = document.createElement("div");
  const root = ReactDOM.createRoot(container);
  root.render(element);

  return new Promise<string>((resolve) => {
    setTimeout(() => {
      resolve(container.innerHTML);
    }, 100);
  });
};

const getRangeBefore = async (context: Word.RequestContext, wordRange: Word.Range) => {
  const startRange = context.document.body.getRange("Start");
  const extendedRangeBefore = startRange.expandTo(wordRange.getRange("Start"));
  extendedRangeBefore.load("text");
  return extendedRangeBefore;
};

const getRangeAfter = async (context: Word.RequestContext, wordRange: Word.Range) => {
  const endRange = context.document.body.getRange("End");
  const extendedRangeAfter = wordRange.getRange("End").expandTo(endRange);
  extendedRangeAfter.load("text");
  return extendedRangeAfter;
};

const getCitationContext = (rangeMap: RangeMap) => {
  const beforeText = rangeMap.beforeRange.text;
  const last200Chars = beforeText.slice(-200);
  const afterText = rangeMap.afterRange.text;
  const first200Chars = afterText.slice(0, 200);
  const wordRangeText = rangeMap.wordRange.text;
  return `${last200Chars} ${wordRangeText} ${first200Chars}`;
};

const getRangeBeforeAndAfterCitation = async (
  context: Word.RequestContext,
  wordRange: Word.Range,
  citation: Citation,
  rangeMap: RangeMap[]
) => {
  const beforeRange = await getRangeBefore(context, wordRange);
  const afterRange = await getRangeAfter(context, wordRange);
  wordRange.load("text");
  rangeMap.push({ beforeRange, wordRange, afterRange, citation });
};

export const highlightCitations = async (citations: Citation[]): Promise<void> => {
  await Word.run(async (context) => {
    const body = context.document.body;
    const searches = [];
    const rangeMap: RangeMap[] = [];
    for (const citation of citations) {
      const citationText = citation.text ?? citation.resource_text;
      // Get the current selection of the document
      // Search for the word in the document
      const searchResults = body.search(citationText);
      // Queue a command to load the searchResults
      context.load(searchResults, "items");
      searches.push({ searchResults, citation });
    }
    await context.sync();

    for (const { searchResults, citation } of searches) {
      if (searchResults.items.length > 0) {
        for (const searchResult of searchResults.items) {
          await getRangeBeforeAndAfterCitation(context, searchResult, citation, rangeMap);
        }
      }
    }

    await context.sync();

    for (const range of rangeMap) {
      const { wordRange, citation } = range;
      const citationContext = getCitationContext(range);
      const citationCopy = { ...citation };
      citationCopy.context = citationContext;
      const contentControl = wordRange.insertContentControl();
      contentControl.tag = JSON.stringify(citationCopy);
      contentControl.appearance = "BoundingBox";
      wordRange.font.highlightColor = "LightGray";
      wordRange.font.color = "Black";
      wordRange.hyperlink = "";
    }
    await context.sync();
  }).catch((error) => {
    console.error("Error highlightCitations:", error);
    throw error; // Rethrow the error for handling upstream if necessary
  });
};
